import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { UsersRound, Plus, Trash2, Loader2, UserPlus, X } from "lucide-react";
import { toast } from "sonner";

export default function Groups() {
  const { selected } = useDomains();
  const did = selected?.id;
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", description: "" });
  const [memberTarget, setMemberTarget] = useState(null);

  const load = useCallback(async () => {
    if (!did) return; setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/groups`); setGroups(data); }
    catch (e) { toast.error(apiError(e)); setGroups([]); } finally { setLoading(false); }
  }, [did]);
  useEffect(() => { load(); }, [load]);

  if (!selected) return <NoDomain />;

  const create = async () => {
    try { await api.post(`/domains/${did}/groups`, form); toast.success("Group created"); setOpen(false); setForm({ email: "", name: "", description: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (g) => { if (!window.confirm(`Delete group ${g.email}?`)) return; try { await api.delete(`/domains/${did}/groups/${g.email}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Groups" subtitle={selected.domain} icon={UsersRound}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="glossy-emerald relative rounded-xl" data-testid="create-group-button"><span className="spec-shine" /><Plus className="mr-1 h-4 w-4" />New Group</Button></DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader><DialogTitle className="font-heading">Create group</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div className="space-y-1.5"><Label>Group email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={`team@${selected.domain}`} data-testid="group-email" /></div>
                <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="group-name" /></div>
                <div className="space-y-1.5"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              </div>
              <DialogFooter><Button onClick={create} className="glossy-emerald relative w-full rounded-xl" data-testid="submit-group"><span className="spec-shine" />Create</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />
      <div className="glass rounded-3xl p-4">
        {loading ? <div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div> :
          groups.length === 0 ? <EmptyState label="No groups" /> :
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {groups.map((g) => (
              <div key={g.id} className="rounded-2xl bg-white/60 p-4" data-testid={`group-${g.email}`}>
                <div className="flex items-start justify-between">
                  <div><p className="font-heading font-semibold text-slate-800">{g.name}</p><p className="text-xs text-slate-500">{g.email}</p></div>
                  <button onClick={() => remove(g)} className="text-rose-400 hover:text-rose-600" data-testid={`delete-group-${g.email}`}><Trash2 className="h-4 w-4" /></button>
                </div>
                <p className="mt-2 text-sm text-slate-600">{g.directMembersCount || 0} members</p>
                <Button size="sm" variant="outline" className="mt-3 w-full rounded-xl bg-white/60" onClick={() => setMemberTarget(g)} data-testid={`manage-members-${g.email}`}><UserPlus className="mr-1 h-3.5 w-3.5" />Members</Button>
              </div>
            ))}
          </div>}
      </div>
      <MembersSheet group={memberTarget} did={did} onClose={() => setMemberTarget(null)} onChanged={load} />
    </div>
  );
}

function MembersSheet({ group, did, onClose, onChanged }) {
  const [members, setMembers] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const reload = useCallback(async () => {
    if (!group) return; setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/groups/${group.email}/members`); setMembers(data); }
    catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [group, did]);
  useEffect(() => { reload(); }, [reload]);
  if (!group) return null;
  const add = async () => { try { await api.post(`/domains/${did}/groups/${group.email}/members`, { email, role: "MEMBER" }); setEmail(""); toast.success("Member added"); reload(); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  const rm = async (m) => { try { await api.delete(`/domains/${did}/groups/${group.email}/members/${m.email}`); toast.success("Removed"); reload(); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <Sheet open={!!group} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full overflow-y-auto sm:max-w-md">
        <SheetHeader><SheetTitle className="font-heading">Members · {group.email}</SheetTitle></SheetHeader>
        <div className="mt-4 flex gap-2">
          <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="member@domain.com" data-testid="add-member-input" />
          <Button onClick={add} className="glossy-emerald relative rounded-xl" data-testid="add-member-button"><span className="spec-shine" />Add</Button>
        </div>
        <div className="mt-4 space-y-1">
          {loading ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : members.map((m) => (
            <div key={m.id || m.email} className="flex items-center justify-between rounded-xl bg-white/60 px-3 py-2 text-sm">
              <div><span className="text-slate-700">{m.email}</span><span className="ml-2 text-xs text-slate-400">{m.role}</span></div>
              <button onClick={() => rm(m)} className="text-rose-400 hover:text-rose-600"><X className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}
